import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Calendar, ChevronLeft, ChevronRight, MapPin, Clock, User, ExternalLink, RefreshCw, Link2 } from 'lucide-react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import { jobsApi, dashboardApi } from '@/lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduledVisits, setScheduledVisits] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState({ configured: false, connected: false });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentMonth]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Fetch scheduled visits
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const visitsRes = await fetch(
        `${API_BASE}/api/calendar/scheduled-visits?start_date=${startDate}&end_date=${endDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const visits = await visitsRes.json();
      setScheduledVisits(visits);

      // Check Google Calendar status
      const statusRes = await fetch(`${API_BASE}/api/calendar/google/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const status = await statusRes.json();
      setCalendarStatus(status);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectGoogleCalendar = async () => {
    setIsConnecting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/calendar/google/auth-url`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('Failed to get authorization URL');
      }
    } catch (error) {
      toast.error('Failed to connect Google Calendar');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSyncToGoogle = async (visit) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/jobs/${visit.id}/sync-to-calendar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Synced to Google Calendar', {
          action: data.event_link ? {
            label: 'Open',
            onClick: () => window.open(data.event_link, '_blank')
          } : undefined
        });
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to sync');
      }
    } catch (error) {
      toast.error('Failed to sync to Google Calendar');
    }
  };

  const renderCalendarHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4">
        <Button variant="ghost" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-lg font-semibold">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        <Button variant="ghost" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const renderDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
      <div className="grid grid-cols-7 mb-2">
        {days.map(day => (
          <div key={day} className="text-center text-xs font-medium text-stone-500 py-2">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const formattedDate = format(day, 'yyyy-MM-dd');
        const dayVisits = scheduledVisits.filter(v => v.start?.startsWith(formattedDate));
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isSelected = isSameDay(day, selectedDate);
        const isToday = isSameDay(day, new Date());

        days.push(
          <div
            key={day.toString()}
            className={`
              min-h-[80px] p-1 border border-stone-100 cursor-pointer transition-colors
              ${!isCurrentMonth ? 'bg-stone-50 text-stone-400' : 'bg-white'}
              ${isSelected ? 'ring-2 ring-[#1F7A78] ring-inset' : ''}
              ${isToday ? 'bg-teal-50' : ''}
              hover:bg-stone-50
            `}
            onClick={() => setSelectedDate(day)}
          >
            <div className={`text-sm font-medium ${isToday ? 'text-[#1F7A78]' : ''}`}>
              {format(day, 'd')}
            </div>
            <div className="space-y-1 mt-1">
              {dayVisits.slice(0, 2).map((visit, idx) => (
                <div
                  key={idx}
                  className="text-xs bg-[#1F7A78]/10 text-[#1F7A78] px-1 py-0.5 rounded truncate"
                  title={visit.title}
                >
                  {visit.job_number}
                </div>
              ))}
              {dayVisits.length > 2 && (
                <div className="text-xs text-stone-400">+{dayVisits.length - 2} more</div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }
    return <div>{rows}</div>;
  };

  const selectedDateVisits = scheduledVisits.filter(v => 
    v.start?.startsWith(format(selectedDate, 'yyyy-MM-dd'))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Calendar</h1>
          <p className="text-sm text-stone-500">Scheduled visits and appointments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {calendarStatus.configured && !calendarStatus.connected && (
            <Button onClick={handleConnectGoogleCalendar} disabled={isConnecting}>
              <Calendar className="h-4 w-4 mr-2" />
              Connect Google Calendar
            </Button>
          )}
          {calendarStatus.connected && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <Calendar className="h-3 w-3 mr-1" />
              Google Calendar Connected
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            {renderCalendarHeader()}
            {renderDays()}
            {renderCells()}
          </CardContent>
        </Card>

        {/* Selected Day Details */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#1F7A78]" />
                {format(selectedDate, 'EEEE, MMMM d')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateVisits.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-4">
                  No visits scheduled for this day
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedDateVisits.map((visit, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-stone-50 rounded-lg border border-stone-100 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1F7A78]">{visit.job_number}</span>
                        <Badge variant="outline" className="text-xs">
                          {visit.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-stone-600">{visit.site_name}</div>
                      
                      {visit.contractor && (
                        <div className="flex items-center gap-1 text-xs text-stone-500">
                          <User className="h-3 w-3" />
                          {visit.contractor}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 pt-2 border-t">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/jobs?job=${visit.id}`)}
                          className="flex-1"
                        >
                          View Job
                        </Button>
                        {calendarStatus.connected && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSyncToGoogle(visit)}
                          >
                            <Link2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Google Calendar Info */}
          {!calendarStatus.configured && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <p className="text-sm text-amber-800">
                  <strong>Google Calendar Integration</strong><br />
                  To enable Google Calendar sync, add your Google API credentials to the server configuration.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">This Month</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-stone-50 rounded-lg">
                  <p className="text-2xl font-bold text-[#1F7A78]">{scheduledVisits.length}</p>
                  <p className="text-xs text-stone-500">Total Visits</p>
                </div>
                <div className="text-center p-3 bg-stone-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">
                    {scheduledVisits.filter(v => v.status === 'Scheduled').length}
                  </p>
                  <p className="text-xs text-stone-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
